/**
 * Pure validator: single source of truth for "is this a valid bare
 * domain we'd accept?". Mirrors the backend's
 * `OrganizationSettingsBase.normalize_allowed_domains` schema validator
 * so the rejection class is identical on both sides; the FE adds the
 * "duplicate" class because the backend sees a single entry per call.
 *
 * Returned shape uses a discriminated union so TS narrows `value` vs
 * `reason` cleanly at the call site; a regression that conflates
 * success and failure into a magic-string-or-null surface would fail
 * type-check before runtime.
 */
// Mirrors the backend's `OrganizationSettingsBase.normalize_allowed_domains`
// rule order so the rejection class is identical across runtimes.
const SHAPE_RULES: ReadonlyArray<{
    predicate: (entry: string) => boolean;
    problem: string;
}> = [
    {
        predicate: (e) => e.includes("@"),
        problem: "looks like an email address",
    },
    { predicate: (e) => e.includes("://"), problem: "contains a URL scheme" },
    { predicate: (e) => e.includes("/"), problem: "contains a path separator" },
    { predicate: (e) => e.includes(":"), problem: "contains a port" },
    {
        predicate: (e) => e.includes(",") || e.includes(";"),
        problem: "contains a list separator",
    },
    { predicate: (e) => /\s/.test(e), problem: "contains whitespace" },
    {
        predicate: (e) => e.startsWith("*"),
        problem: "uses a wildcard (not supported)",
    },
    {
        predicate: (e) => e.startsWith(".") || e.endsWith("."),
        problem: "has a leading or trailing dot",
    },
    {
        predicate: (e) => !e.includes("."),
        problem: "is not a valid domain (missing '.')",
    },
];

export function validateDomain(
    raw: string,
    existing: readonly string[]
): { ok: true; value: string } | { ok: false; reason: string } {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed === "") {
        return {
            ok: false,
            reason: "Enter a domain (e.g. acme.com); whitespace is not a domain.",
        };
    }
    for (const { predicate, problem } of SHAPE_RULES) {
        if (predicate(trimmed)) {
            return {
                ok: false,
                reason: `"${raw.trim()}" ${problem}; use the bare domain (e.g. acme.com).`,
            };
        }
    }
    if (existing.some((entry) => entry.toLowerCase() === trimmed)) {
        return {
            ok: false,
            reason: `"${trimmed}" is already in the list (duplicate).`,
        };
    }
    return { ok: true, value: trimmed };
}
